/**
 * Shared motion vocabulary — the ops-console motion doctrine in code.
 *
 * Motion here is calm and purposeful: information arriving, never decoration.
 * - One easing family (EASE_OUT — the PageTransition curve). No bounce/spring.
 * - Three durations: micro (150ms) / standard (200ms) / orchestration (300ms).
 * - Animate on ROUTE ENTER and USER ACTION only — never on data refetch/poll.
 *   Keep motion wrappers ABOVE any data-dependent `key` so a React Query
 *   refetch can never remount (and re-animate) them.
 * - Guard with `usePrefersReducedMotion()` at every call site:
 *   `variants={prefersReducedMotion ? undefined : staggerContainer}`.
 *
 * See docs/superpowers/specs/2026-07-02-motion-pass.md.
 */
import type { Variants } from 'framer-motion';

/** The app's single easing family — cubic-bezier(0.25, 0.1, 0.25, 1). */
export const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

/** Doctrine durations in seconds (framer-motion units). */
export const DUR = {
  /** 150ms — micro-interactions (modal pop, small fades). */
  micro: 0.15,
  /** 200ms — standard entrances (page/status ledes). */
  standard: 0.2,
  /** 300ms — orchestrated section entrances. */
  orchestration: 0.3,
} as const;

/**
 * Route-enter orchestration: parent container staggers its children.
 * Apply to a wrapper that mounts once per route — never to a list whose
 * items (or key) change with query data.
 */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

/** A staggered child: fades up 12px over the orchestration duration. */
export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DUR.orchestration, ease: EASE_OUT },
  },
};
